import type {ReactNode} from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/modules/foundation/components/accordion';
import {ContentBlockHeading} from '@/modules/foundation/components/content-block-heading';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';
import type {KeyedArray} from '@/modules/foundation/shared/types';
import {
	ContentBlockContainer,
	type ContentBlockContainerProps,
} from '@/modules/page/components/content-blocks/block-container';

import {DOCUMENT_TYPE_FAQ_FRONTPAGE} from '../../../sanity/frontpage/constants';
import type {FAQ} from '../../../types';

interface Props extends ContentBlockContainerProps {
	faqs?: KeyedArray<FAQ>;
	heading?: string;
	description?: ReactNode;
	anchorHeadings?: boolean;
	insideToc?: boolean;
}

export const faqListSelectionPortableTextOverrides: PortableTextOverrides = {
	p: 'text-xl',
};

export const FaqListSelection = ({
	faqs,
	heading,
	description,
	anchorHeadings,
	options,
	insideToc,
}: Props) => {
	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<div className={clsx('grid', '@2xl:grid-cols-12', '@2xl:gap-10', 'relative')}>
					<div className="@2xl:col-span-4">
						<div className={clsx('@2xl:sticky', '@2xl:top-8', 'mb-9')}>
							<Image
								src="/images/faq-heading.svg"
								alt=""
								width={105}
								height={105}
								className={clsx('mb-10')}
							/>
							<ContentBlockHeading heading={heading} anchorHeading={anchorHeadings}>
								{description}
							</ContentBlockHeading>
						</div>
					</div>
					<div className="@2xl:col-span-7 @2xl:-col-end-1">
						<Accordion type="single" collapsible className="mb-6">
							{faqs?.map((faq) => (
								<AccordionItem key={faq._key} value={faq._key}>
									<AccordionTrigger>{faq.question}</AccordionTrigger>
									<AccordionContent>{faq.answer}</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
						<Link
							href={internalLinkToHref(DOCUMENT_TYPE_FAQ_FRONTPAGE)}
							className={clsx(
								'text-link',
								'hover:text-primary-5',
								'font-medium',
								'inline-flex',
								'items-center',
								'gap-2',
								'transition-colors',
							)}
						>
							Alle spørsmål og svar
							<ArrowRightIcon
								aria-hidden
								className={clsx('text-grey-25', 'text-[11px]', 'shrink-0')}
							/>
						</Link>
					</div>
				</div>
			</div>
		</ContentBlockContainer>
	);
};
